import type { IDBPDatabase } from 'idb';
import { type LearningEntry, type LearningStore, recordChoice } from '@burfy/input-tools';
import { type BurfyDocsDB, LEARNING_STORE, openBurfyDocsDb } from './database.ts';

/**
 * Learned transliteration choices, kept on this device.
 *
 * There is no server, no shared model and nothing leaves the browser — the
 * only thing remembered is which candidate this person picked for which roman
 * word, so that choice comes first next time.
 */
export class IndexedDbLearningStore implements LearningStore {
  #db: Promise<IDBPDatabase<BurfyDocsDB>> | null = null;
  readonly #databaseName: string | undefined;

  constructor(options: { databaseName?: string } = {}) {
    this.#databaseName = options.databaseName;
  }

  #connection(): Promise<IDBPDatabase<BurfyDocsDB>> {
    this.#db ??= openBurfyDocsDb(this.#databaseName);
    return this.#db;
  }

  async close(): Promise<void> {
    const pending = this.#db;
    this.#db = null;
    if (pending) (await pending).close();
  }

  async get(key: string): Promise<LearningEntry | undefined> {
    try {
      const db = await this.#connection();
      return (await db.get(LEARNING_STORE, key))?.counts;
    } catch (error) {
      // Ranking works without history — degrade quietly rather than break
      // typing because storage is unavailable (private mode, quota, etc.).
      console.warn('[burfy-docs] could not read learned choices', error);
      return undefined;
    }
  }

  async record(key: string, candidate: string): Promise<void> {
    try {
      const db = await this.#connection();
      // Read and write in one transaction: two quick choices for the same word
      // would otherwise both read the same count and one increment would vanish.
      const tx = db.transaction(LEARNING_STORE, 'readwrite');
      const existing = await tx.store.get(key);
      await tx.store.put({ key, counts: recordChoice(existing?.counts, candidate) });
      await tx.done;
    } catch (error) {
      console.warn('[burfy-docs] could not save a learned choice', error);
    }
  }
}
